import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { GitDiff } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const TONE = {
  reconciled: "bg-emerald-50 text-emerald-700",
  variance: "bg-rose-50 text-rose-700",
  no_subledger_data: "bg-slate-100 text-slate-500",
};

export default function SubledgerReconPanel() {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/finance/gl/reconciliation/subledgers",
        { params: asOf ? { as_of_period: asOf } : {} });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="dc-card overflow-hidden" data-testid="subrecon-panel">
      <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-2">
        <GitDiff size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Subledger ↔ GL Control Reconciliation</h3>
        {data && (
          <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500" data-testid="subrecon-summary">
            {data.summary.reconciled}/{data.summary.total} reconciled · {data.summary.variances} variance(s)
          </span>
        )}
        <div className="ml-auto flex items-end gap-2">
          <div>
            <Label className="text-xs">As of period</Label>
            <Input type="month" className="h-8 w-40" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="subrecon-asof" />
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="subrecon-refresh">Refresh</Button>
        </div>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[880px]" data-testid="subrecon-table">
          <thead><tr><th>Area</th><th>Control accounts</th><th className="text-right">GL balance</th><th className="text-right">Subledger</th><th className="text-right">Difference</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.area} data-testid={`subrecon-row-${r.area.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-5 py-3 text-sm font-semibold text-slate-900">{r.area}<div className="text-[10px] font-normal text-slate-400">{r.note}</div></td>
                <td className="font-mono-alt text-xs">{r.control_accounts.join(", ")}</td>
                <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(r.gl_balance)}</td>
                <td className="text-right font-mono-alt text-xs tabular-nums">{r.subledger_balance == null ? "—" : formatCurrency(r.subledger_balance)}</td>
                <td className={`text-right font-mono-alt text-xs tabular-nums font-semibold ${r.difference && Math.abs(r.difference) > 0.01 ? "text-rose-600" : "text-slate-500"}`}>
                  {r.difference == null ? "—" : formatCurrency(r.difference)}
                </td>
                <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[r.status]}`}>{r.status === "no_subledger_data" ? "NO DATA" : r.status.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
